import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { CloudOff, ArrowRight, Sun, Cloud, CloudSun } from 'lucide-react';
import { DailyEnvironmentalDataService } from '../../../features/manage/services/dailyEnvironmentalData.service';
import { subscribeToEnvironmentalDataUpdates } from '../../utils/environmentalDataEvents';
import { useAuth } from '../../../features/auth';
import type { DailyEnvironmentalData, WeatherCondition } from '../../../types/api';

const WEATHER_ICONS: Record<WeatherCondition, React.ElementType> = {
  clear: Sun,
  cloudy: Cloud,
  sunny: CloudSun,
};

interface EnvironmentalDataWidgetProps {
  /** `topbar` restyles the chip for the dark blue top bar. */
  variant?: 'default' | 'topbar';
}

/**
 * Persistent navbar widget showing today's environmental reading (weather-app
 * style: icon + temperature, hover for the full breakdown). If nothing has
 * been entered for today, shows a cloud-off icon; hovering explains it's not
 * set, and clicking either the icon or the hover card's "Click to set" link
 * navigates to Manage > Environmental Data.
 */
const EnvironmentalDataWidget: React.FC<EnvironmentalDataWidgetProps> = ({
  variant = 'default',
}) => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManageEnvironmentalData = hasRole('admin');
  const [data, setData] = useState<DailyEnvironmentalData | null>(null);
  const [isMissing, setIsMissing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const onTopbar = variant === 'topbar';

  const fetchToday = useCallback(async () => {
    try {
      const result = await DailyEnvironmentalDataService.getToday();
      setData(result);
      setIsMissing(false);
    } catch (error) {
      setData(null);
      setIsMissing(true);
    }
  }, []);

  useEffect(() => {
    fetchToday();
    return subscribeToEnvironmentalDataUpdates(fetchToday);
  }, [fetchToday]);

  if (isMissing) {
    const iconButtonClasses = onTopbar
      ? 'flex items-center justify-center h-9 w-9 rounded-full border border-white/35 bg-white/12 text-white transition-colors' +
        (canManageEnvironmentalData ? ' hover:bg-white/20 hover:border-white/60' : ' cursor-default')
      : 'flex items-center justify-center h-9 w-9 rounded-full bg-gray-100 text-gray-500 transition-colors' +
        (canManageEnvironmentalData ? ' hover:bg-gray-200 hover:text-gray-700' : ' cursor-default');

    return (
      <div
        className="relative"
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
      >
        <button
          type="button"
          onClick={
            canManageEnvironmentalData
              ? () => navigate('/manage/daily-environmental-data')
              : undefined
          }
          className={iconButtonClasses}
          aria-label={
            canManageEnvironmentalData
              ? "Today's environmental data has not been entered — click to set it"
              : "Today's environmental data has not been entered"
          }
        >
          <CloudOff className="h-4 w-4" />
        </button>

        {isHovering && (
          <div
            className="absolute right-0 top-full z-50 mt-2 w-56 rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
            style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)' }}
          >
            <p className="text-sm font-medium text-gray-900">Environmental data not set</p>
            <p className="mt-1 text-xs text-gray-500">
              Required before starting a calibration today.
            </p>
            {canManageEnvironmentalData ? (
              <button
                type="button"
                onClick={() => navigate('/manage/daily-environmental-data')}
                className="mt-3 flex items-center gap-1 text-sm font-medium text-primary-600 hover:text-primary-700"
              >
                Click to set
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <p className="mt-3 text-xs text-gray-500">Please ask an admin to set it.</p>
            )}
          </div>
        )}
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const Icon = WEATHER_ICONS[data.weather_condition] || CloudSun;

  return (
    <div
      className="relative"
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
    >
      <div
        className={
          onTopbar
            ? 'flex cursor-default items-center gap-1.5 rounded-full border border-white/35 bg-white/12 px-3 py-1.5 text-[0.78rem] font-semibold text-white'
            : 'flex cursor-default items-center gap-1.5 rounded-full bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-700'
        }
      >
        <Icon className={onTopbar ? 'h-4 w-4 text-white' : 'h-4 w-4 text-primary-600'} />
        <span>{data.ambient_temperature}°C</span>
      </div>

      {isHovering && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-64 rounded-lg border border-gray-100 bg-white p-4 shadow-lg"
          style={{ boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)' }}
        >
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            {data.entry_date}
          </p>
          <hr className="my-3 border-gray-100" />
          <div className="space-y-2.5">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Ambient Temperature</span>
              <span className="text-sm font-medium text-gray-900">
                {data.ambient_temperature}°C
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Humidity</span>
              <span className="text-sm font-medium text-gray-900">
                {data.humidity != null ? `${data.humidity}%` : '—'}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Atmospheric Pressure</span>
              <span className="text-sm font-medium text-gray-900">
                {data.atm_pressure_mmhg} mmHg
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-500">Weather Condition</span>
              <span className="flex items-center gap-1.5 text-sm font-medium capitalize text-gray-900">
                <Icon className="h-4 w-4 text-primary-600" />
                {data.weather_condition}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentalDataWidget;
